import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Button, Spinner, spinnerMargins, Text } from '@bai/components';
import { logout } from '@bai/features/user';
import { getContrastTextColor, useTheme } from '@bai/utils/theme';

import LogoBlackImage from '../../../../../components/main/assets/icons/logo-black.png';
import { Footer } from '../footer/footer';

import { ForgotPassword } from './ForgotPassword';
import {
  handleLoginFeatureSubmit,
  loginFeatureValidator,
} from './loginFeatureSubmitHandler';
import { ValidationMessageType, ValidationType } from './loginFeatureTypes';
import { NewPassword } from './NewPassword';

import type { UserThemeResponse } from './types';

export interface LoginPageProps {
  portal?: string;
  themeIsLoading: boolean;
  customTheme?: UserThemeResponse;
  handleLogin: (
    event: React.SyntheticEvent<HTMLFormElement>,
    username: string,
    password: string,
    handleValidation: () => void,
  ) => Promise<void>;
  loggingIn: boolean;
  resetPasswordRequired: boolean;
  loginError?: string;
  weakPassword: boolean;
}

const defaultBackgroundColor = '#00375d';
const defaultFont = 'Calibri, sans-serif !important';

const listItemStyle = {
  '& .MuiTypography-root': {
    fontFamily: 'Calibri, sans-serif !important',
  },
};

function CustomLoginTextWintrust(portal?: string) {
  if (portal === 'wintrust-com') {
    return (
      <Grid
        container
        data-cy="wintrust-custom-text"
        style={{ fontFamily: defaultFont, fontSize: '1vw', marginTop: '2em' }}
      >
        <p>
          <b>IMPORTANT:</b> The format for your University username is W +
          Workday Employee Number. <i>Example:</i> w12345
        </p>
        <p>
          If you do not receive an email when using the password reset tool,
          please contact our vendor (ProSight) at 800.264.7600x1
        </p>
      </Grid>
    );
  }
  return '';
}

function CustomLoginBranding() {
  return (
    <Grid size={6} data-cy="powered-by-bai">
      <Grid
        container
        sx={{ justifyContent: 'flex-end', alignContent: 'center' }}
      >
        <Grid size={6} sx={{ marginTop: '1em', justifyContent: 'flex-end' }}>
          <Grid container sx={{ justifyContent: 'flex-end' }}>
            <Text
              sx={{
                fontFamily: defaultFont,
                fontSize: '1.4em',
              }}
            >
              Powered by
            </Text>
          </Grid>
        </Grid>
        <Grid
          size={3}
          sx={{ marginTop: '1em', marginLeft: '0.5em', marginRight: '3em' }}
        >
          <img src={LogoBlackImage} height="20px" alt="powered-by-bai-logo" />
        </Grid>
      </Grid>
    </Grid>
  );
}

export function LoginPage({
  portal,
  themeIsLoading,
  customTheme,
  handleLogin,
  loggingIn,
  resetPasswordRequired,
  loginError,
  weakPassword,
}: LoginPageProps) {
  const theme = useTheme();

  const primaryColor =
    customTheme?.PrimaryColor ?? theme.branding?.primaryColor ?? '#00375d';

  const textColor = getContrastTextColor(primaryColor);

  const loginPageStyle = {
    backgroundColor: customTheme?.PrimaryColor ?? defaultBackgroundColor,
    minHeight: '100vh',
    fontFamily: defaultFont,
  };
  const linkStyle = {
    color: `${textColor} !important`,
    fontFamily: defaultFont,
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(resetPasswordRequired);
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [validationResponse, setValidationResponse] = useState({
    password: 'valid',
    username: 'valid',
  });
  const [errorMsg, setErrorMsg] = useState(loginError);
  const [hasWeakPassword, setHasWeakPassword] = useState(false);

  useEffect(() => {
    setHasWeakPassword(weakPassword);
    setShowNewPassword(weakPassword);
  }, [weakPassword]);

  const usernameInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowNewPassword(resetPasswordRequired);
  }, [resetPasswordRequired]);

  useEffect(() => {
    setErrorMsg(loginError);
  }, [loginError]);

  let usernameValid =
    loginFeatureValidator(
      ValidationType.DEFAULTS_TO_EMPTY,
      null,
      username === '' &&
        usernameInput.current &&
        usernameInput.current?.value !== undefined
        ? usernameInput.current?.value
        : username,
      null,
    ) && validationResponse.username === 'valid';

  let passwordValid =
    loginFeatureValidator(
      ValidationType.DEFAULTS_TO_EMPTY,
      null,
      password === '' &&
        passwordInput.current &&
        passwordInput.current?.value !== undefined
        ? passwordInput.current?.value
        : password,
      null,
    ) && validationResponse.password === 'valid';

  const handleUsernameCheck = () => {
    usernameValid = loginFeatureValidator(
      ValidationType.IS_REQUIRED,
      null,
      username === '' &&
        usernameInput.current &&
        usernameInput.current?.value !== undefined
        ? usernameInput.current?.value
        : username,
      null,
    );

    if (!usernameValid) {
      setValidationResponse({
        ...validationResponse,
        username: 'invalid',
      });
    } else {
      setValidationResponse({ ...validationResponse, username: 'valid' });
    }
  };

  const handlePasswordCheck = () => {
    passwordValid = loginFeatureValidator(
      ValidationType.IS_REQUIRED,
      null,
      password === '' &&
        passwordInput.current &&
        passwordInput.current?.value !== undefined
        ? passwordInput.current?.value
        : password,
      null,
    );

    if (!passwordValid) {
      setValidationResponse({
        ...validationResponse,
        password: 'invalid',
      });
    } else {
      setValidationResponse({ ...validationResponse, password: 'valid' });
    }
  };

  const handleUsernameInputEvent = () => {
    handleUsernameCheck();
  };

  const handlePasswordInputEvent = () => {
    handlePasswordCheck();
  };

  const handleUsernameChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    e.persist();

    // added for Firefox
    if (usernameInput.current) {
      usernameInput.current.value = e.target.value.trim();
    }
    setUserName(e.target.value.trim());
    handleUsernameCheck();
  };

  const handlePasswordChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    e.persist();

    // added for Firefox
    if (passwordInput.current) {
      passwordInput.current.value = e.target.value;
    }
    setPassword(e.target.value);
    handlePasswordCheck();
  };

  const handleValidation = () => {
    handleUsernameCheck();
    handlePasswordCheck();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCancelForgotPassword = () => {
    setUserName('');
    setShowForgotPassword(false);
  };

  const handleCancelNewPassword = async () => {
    setShowNewPassword(false);
    setShowForgotPassword(false);

    // must log user out entering valid temp password authenticates a user
    let loginView = '/';

    await logout(portal ?? '');

    const portalCode = portal as string;

    if (portalCode !== '') {
      loginView = `${loginView}?portal=${portalCode}`;
    }

    window.location.href = loginView;
  };

  const forgotPasswordProps = {
    primaryColor: customTheme?.PrimaryColor || theme.branding?.primaryColor,
    logo: customTheme?.Logo?.toLowerCase() || theme.branding?.logo,
    handleCancel: handleCancelForgotPassword,
    handleSubmit: handleLoginFeatureSubmit,
    portalCode: portal || '',
    loginUsername: username,
    hasCustomLogo: !!customTheme?.Logo,
  };

  const newPasswordFormProps = {
    ...forgotPasswordProps,
    handleCancel: handleCancelNewPassword,
    username,
    hasWeakPassword,
    hasCustomLogo: !!customTheme?.Logo,
  };

  const [logoLoaded, setLogoLoaded] = useState(false);
  const handleImageLoaded = () => {
    setLogoLoaded(true);
  };

  return !!portal && themeIsLoading ? (
    <Grid
      container
      sx={{
        ...loginPageStyle,
        justifyContent: 'center',
        alignItems: 'center',
        direction: 'row',
      }}
    >
      <Box>
        <Spinner data-cy="loading-spinner" />
      </Box>
    </Grid>
  ) : (
    <Grid id="login-features" sx={loginPageStyle}>
      <Grid container columns={12}>
        <Grid size={4} />

        <Grid
          size={4}
          container
          sx={{ alignItems: 'center', minHeight: '90vh' }}
        >
          <Grid container>
            <Grid container sx={{ justifyContent: 'center', width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#FFFFFF !important',
                  boxShadow: 10,
                  marginTop: '2em',
                  borderRadius: '25px',
                  width: '100%',
                }}
              >
                {showNewPassword && (
                  <Grid
                    sx={{
                      maxWidth: '80%',
                      margin: 'auto',
                      paddingTop: '2em',
                      paddingBottom: '2em',
                    }}
                  >
                    <NewPassword {...newPasswordFormProps} />
                  </Grid>
                )}
                {showForgotPassword && (
                  <Grid
                    sx={{
                      maxWidth: '80%',
                      margin: 'auto',
                      paddingTop: '2em',
                      paddingBottom: '2em',
                    }}
                  >
                    <ForgotPassword {...forgotPasswordProps} />
                  </Grid>
                )}

                {!showNewPassword && !showForgotPassword && (
                  <form
                    onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
                      handleLogin(event, username, password, handleValidation)
                    }
                  >
                    <Grid
                      sx={{
                        maxWidth: '80%',
                        margin: 'auto',
                        paddingTop: '2em',
                        paddingBottom: '2em',
                      }}
                    >
                      <Grid container sx={{ justifyContent: 'center' }}>
                        <Grid
                          container
                          sx={{
                            justifyContent: 'center',
                            width: customTheme?.Logo?.toLowerCase()
                              ? '40%'
                              : '60%',
                          }}
                        >
                          <img
                            src={
                              // seems to be an issue with some image paths when not lowercase
                              customTheme?.Logo?.toLowerCase() ??
                              theme?.branding?.logo
                            }
                            alt="logo"
                            className="themed_logo"
                            style={{
                              display: logoLoaded ? '' : 'none',
                            }}
                            onLoad={handleImageLoaded}
                            data-cy="logo"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        sx={{ justifyContent: 'center', marginY: '1.5em' }}
                      >
                        <Text
                          sx={{ fontSize: '2vw', fontFamily: defaultFont }}
                          data-cy="login-header"
                        >
                          Log in to your online learning
                        </Text>
                        {CustomLoginTextWintrust(portal)}
                      </Grid>
                      <TextField
                        name="username"
                        data-cy="username"
                        id="username"
                        label="Username"
                        variant="outlined"
                        type="text"
                        autoComplete="username"
                        ref={usernameInput}
                        onChange={(
                          event: React.ChangeEvent<
                            HTMLTextAreaElement | HTMLInputElement
                          >,
                        ) => handleUsernameChange(event)}
                        onMouseUp={handleUsernameInputEvent}
                        onKeyUp={handleUsernameInputEvent}
                        error={!usernameValid}
                        helperText={
                          !usernameValid
                            ? `Username ${ValidationMessageType.IS_REQUIRED}`
                            : ''
                        }
                        sx={{ marginBottom: '0.5em', width: '100%' }}
                        slotProps={{
                          input: {
                            inputProps: {
                              role: 'textbox',
                              'aria-label': 'Username',
                            },
                          },
                        }}
                      />
                      <TextField
                        name="password"
                        data-cy="password"
                        // do not change the id="password" because pingdom uses it.
                        id="password"
                        label="Password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        ref={passwordInput}
                        sx={{
                          fontFamily: 'Calibri !important',
                          width: '100%',
                        }}
                        onChange={(
                          event: React.ChangeEvent<
                            HTMLTextAreaElement | HTMLInputElement
                          >,
                        ) => handlePasswordChange(event)}
                        onMouseUp={handlePasswordInputEvent}
                        onKeyUp={handlePasswordInputEvent}
                        error={!passwordValid}
                        helperText={
                          !passwordValid
                            ? `Password ${ValidationMessageType.IS_REQUIRED}`
                            : ''
                        }
                        slotProps={{
                          input: {
                            inputProps: {
                              role: 'password',
                              'aria-label': 'Password',
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  data-cy="password-visibility"
                                >
                                  {showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                      {errorMsg && (
                        <Grid
                          container
                          className="alert alert-danger"
                          sx={{
                            alignItems: 'center',
                            fontFamily: defaultFont,
                          }}
                        >
                          <Grid
                            size={12}
                            sx={{ textAlign: 'left' }}
                            key="error-message"
                          >
                            {errorMsg}
                          </Grid>
                        </Grid>
                      )}
                      <Button
                        data-cy="login-button"
                        type="submit"
                        specificColorToUse={
                          customTheme?.PrimaryColor ??
                          theme.branding?.primaryColor
                        }
                        sx={{
                          display: loggingIn ? 'none' : '',
                        }}
                      >
                        Log In
                      </Button>
                      {loggingIn && !errorMsg && (
                        <Spinner
                          data-cy="login-spinner"
                          margin={spinnerMargins.none}
                        />
                      )}
                      <Grid container>
                        <Grid size={6}>
                          <Grid sx={{ marginTop: '1.5em' }}>
                            <button
                              type="button"
                              onClick={() => setShowForgotPassword(true)}
                              className="as-link"
                              data-cy="forgot-password"
                            >
                              Forgot Password?
                            </button>
                          </Grid>
                        </Grid>
                        {customTheme?.Logo && CustomLoginBranding()}
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Box>
            </Grid>
            <Grid container sx={{ justifyContent: 'center', width: '100%' }}>
              <Grid sx={{ width: '100%' }}>
                <Text
                  data-cy="maintenance-text"
                  sx={{
                    color: `${textColor} !important`,
                    fontFamily: defaultFont,
                    fontSize: '1.2em',
                    paddingX: '1em',
                    marginX: 'auto',
                    marginTop: '.5em',
                  }}
                >
                  The ProSight Learning Manager may be offline on Sundays from 6
                  - 9 am CT for scheduled maintenance.
                </Text>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={4} container>
          <Grid
            container
            sx={{
              justifyContent: 'right',
              fontFamily: defaultFont,
              width: '100%',
            }}
          >
            <List dense>
              <ListItem
                component="a"
                href="https://www.prosightfa.org"
                sx={linkStyle}
                data-cy="bai-org-link"
              >
                <ListItemText sx={listItemStyle}>
                  Visit ProSightFA.org
                </ListItemText>
              </ListItem>
              <ListItem
                component="a"
                href="https://www.bai.org/compliance-and-training/policy-manager"
                sx={linkStyle}
                data-cy="pm-link"
              >
                <ListItemText sx={listItemStyle}>
                  ProSight Policy Manager
                </ListItemText>
              </ListItem>
              <ListItem
                component="a"
                href="https://www.bai.org/banking-strategies"
                sx={linkStyle}
                data-cy="banking-strategies-link"
              >
                <ListItemText sx={listItemStyle}>
                  Banking Strategies
                </ListItemText>
              </ListItem>
              <ListItem
                component="a"
                href="https://www.bai.org/banking-strategies/?_format=webinar"
                sx={linkStyle}
                data-cy="webinar-link"
              >
                <ListItemText sx={listItemStyle}>Webinars</ListItemText>
              </ListItem>
              <ListItem
                component="a"
                href="https://www.bai.org/banking-strategies/?_format=podcast"
                sx={linkStyle}
                data-cy="podcast-link"
              >
                <ListItemText sx={listItemStyle}>Podcasts</ListItemText>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Grid>

      <Footer hideBorder textColor={textColor} />
    </Grid>
  );
}

export default LoginPage;
